'use client';

import { useCallback, useMemo } from 'react';
import { isNil } from 'es-toolkit';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';

import type { TaxEnrollmentStatus, TaxEnrollmentTypeName } from '@savvy/constants/generated/gql-enums';

const sortByValues = ['client_name', 'tax_year', 'tax_status', 'price', 'filing_type'] as const;
const sortByOrderValues = ['ASC', 'DESC'] as const;

const filingTypeValues = ['savvy_single', 'savvy_joint', 'external_cpa', 'other', 'transcript_services_only'] as const;
const statusValues = [
  'federal_accepted',
  'federal_rejected',
  'federal_submitted',
  'in_progress',
  'not_interested',
  'not_started',
  'registered',
  'state_accepted',
  'state_rejected',
  'state_submitted',
] as const satisfies TaxEnrollmentStatus[];
export const taxYearValues = ['2025', '2026'] as const;

export type SortBy = (typeof sortByValues)[number];
export type SortByOrder = (typeof sortByOrderValues)[number];
export type TaxYear = (typeof taxYearValues)[number];

export function assertSortBy(value: string): asserts value is SortBy {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (!sortByValues.includes(value as SortBy)) {
    throw new Error(`Invalid sort by: ${value}`);
  }
}

export function assertSortByOrder(value: string): asserts value is SortByOrder {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (!sortByOrderValues.includes(value as SortByOrder)) {
    throw new Error(`Invalid sort by order: ${value}`);
  }
}

function isValidTaxYear(value: string): value is TaxYear {
  return value === '2025' || value === '2026';
}

export const taxOverviewUrlParsers = {
  searchQuery: parseAsString,
  sortBy: parseAsStringLiteral(sortByValues).withDefault('client_name'),
  sortByOrder: parseAsStringLiteral(sortByOrderValues).withDefault('ASC'),

  pageSize: parseAsInteger.withDefault(25),
  currentPage: parseAsInteger.withDefault(0),
  after: parseAsString,
  before: parseAsString,

  requireAdvisorActionFilter: parseAsBoolean,
  requireClientActionFilter: parseAsBoolean,
  waitingOnThirdPartyFilter: parseAsBoolean,

  enrollmentTypeFilter: parseAsArrayOf(parseAsStringLiteral(filingTypeValues)),
  statusFilter: parseAsArrayOf(parseAsStringLiteral(statusValues)),
  taxYearFilter: parseAsArrayOf(parseAsStringLiteral(taxYearValues)),
} as const;

export function useTaxOverviewParams() {
  const [params, setParams] = useQueryStates(taxOverviewUrlParsers);

  const updateParams = useCallback(
    (updates: Partial<typeof params>) => {
      setParams({
        before: null,
        after: null,
        currentPage: 0,
        ...updates,
      });
    },
    [setParams],
  );

  const helpers = useMemo(
    () => ({
      setRequireAdvisorActionFilter: (value: boolean) =>
        updateParams({
          requireAdvisorActionFilter: value,
          requireClientActionFilter: null,
          waitingOnThirdPartyFilter: null,
        }),
      setRequireClientActionFilter: (value: boolean) =>
        updateParams({
          requireAdvisorActionFilter: null,
          requireClientActionFilter: value,
          waitingOnThirdPartyFilter: null,
        }),
      setWaitingOnThirdPartyFilter: (value: boolean) =>
        updateParams({
          requireAdvisorActionFilter: null,
          requireClientActionFilter: null,
          waitingOnThirdPartyFilter: value,
        }),
      setSortBy: (newSortBy: SortBy) => {
        if (params.sortBy === newSortBy) {
          const newOrder = params.sortByOrder === 'ASC' ? 'DESC' : 'ASC';
          updateParams({ sortByOrder: newOrder });
        } else {
          updateParams({ sortBy: newSortBy, sortByOrder: 'ASC' });
        }
      },
      setSortByOrder: (newSortByOrder: SortByOrder) => updateParams({ sortByOrder: newSortByOrder }),
      setPageSize: (size: number) => updateParams({ pageSize: size }),
      setAfter: (value: string | null) => updateParams({ after: value }),
      setBefore: (value: string | null) => updateParams({ before: value }),
      resetPagination: () => updateParams({ after: null, before: null }),
      goToNextPage: (endCursor: string | null) =>
        setParams({ after: endCursor, before: null, currentPage: params.currentPage + 1 }),
      goToPreviousPage: (startCursor: string | null) =>
        setParams({ before: startCursor, after: null, currentPage: Math.max(0, params.currentPage - 1) }),
      setSearchQuery: (value: string) => updateParams({ searchQuery: value, before: null, after: null }),
      setEnrollmentTypeFilter: (value: TaxEnrollmentTypeName[] | null) =>
        updateParams({ enrollmentTypeFilter: value, before: null, after: null }),
      setStatusFilter: (value: TaxEnrollmentStatus[] | null) =>
        updateParams({ statusFilter: value, before: null, after: null }),
      setTaxYearFilter: (value: string[] | null) => {
        const validYears = value?.filter(isValidTaxYear) ?? null;
        updateParams({ taxYearFilter: validYears, before: null, after: null });
      },
      clearAllFilters: () =>
        updateParams({
          before: null,
          after: null,
          searchQuery: null,
          requireAdvisorActionFilter: null,
          requireClientActionFilter: null,
          waitingOnThirdPartyFilter: null,
          enrollmentTypeFilter: null,
          statusFilter: null,
          taxYearFilter: null,
        }),
    }),
    [updateParams, setParams, params.sortBy, params.sortByOrder, params.currentPage],
  );

  const pagination = useMemo(
    () => ({
      first: !isNil(params.before) ? null : params.pageSize,
      last: !isNil(params.before) ? params.pageSize : null,
      after: !isNil(params.before) ? null : params.after,
      before: !isNil(params.before) ? params.before : null,
    }),
    [params],
  );

  return {
    params,
    updateParams,
    ...helpers,
    pagination,
  };
}
